/**
 * Error Boundary Component
 *
 * Catches JavaScript errors in child components and displays
 * a fallback UI instead of crashing the entire app.
 */

import { Component, type ReactNode } from 'react'
import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi'
import { Button } from '../ui/button'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex min-h-[400px] items-center justify-center p-8">
          <div className="flex flex-col items-center gap-4 text-center max-w-[400px]">
            <div className="text-error">
              <FiAlertTriangle size={48} />
            </div>
            <p className="text-xl font-semibold text-foreground">
              Something went wrong
            </p>
            <p className="text-foreground-muted">
              An unexpected error occurred. Please try again or refresh the page.
            </p>
            {this.state.error && (
              <div className="p-3 bg-background-secondary rounded-[var(--radius-button)] w-full max-h-[100px] overflow-auto">
                <p className="text-sm font-mono text-error">
                  {this.state.error.message}
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={this.handleRetry}
              >
                <FiRefreshCw className="mr-2" />
                Try Again
              </Button>
              <Button
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
